import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular/messages';
import { TranslateService } from '@ngx-translate/core';
import { catchError, concatMap, exhaustMap, from, map, of, switchMap, tap, toArray } from 'rxjs';
import { WarehouseError } from '@app/store/shared/models';
import { unassignedBoxesActions } from '../actions/unassigned-boxes.actions';
import { HttpService } from '../services/http.service';
import { finalizeLoader } from '@app/shared/components';

@Injectable()
export class UnassignedBoxesEffects {
    private readonly actions$ = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);

    loadBoxes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(unassignedBoxesActions.loadBoxes),
            switchMap(({ page }) => {
                return this.httpService.loadBoxes(page).pipe(
                    finalizeLoader('unassigned-boxes-load'),
                    map(({ boxes, hasMore }) => unassignedBoxesActions.loadBoxesSuccess({ payload: boxes, page, hasMore })),
                    catchError((error: HttpErrorResponse) => of(unassignedBoxesActions.loadBoxesError({ code: error.status }))),
                );
            }),
        );
    });

    generateBoxes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(unassignedBoxesActions.generateBoxes),
            exhaustMap(({ quantity }) => {
                return this.httpService.generateBoxes(quantity).pipe(
                    finalizeLoader('unassigned-boxes-generate'),
                    map((payload) => unassignedBoxesActions.generateBoxesSuccess({ payload })),
                    catchError((error: WarehouseError) => {
                        if (error.reason === 'duplicate_request') {
                            this.messageService.add(this.translateService.instant(error.key), 'warning');
                            return of(unassignedBoxesActions.loadBoxes({ page: 1 }));
                        }
                        this.messageService.add(this.translateService.instant(error.key), 'danger');
                        return of(unassignedBoxesActions.generateBoxesError({ code: error.code, key: error.key }));
                    }),
                );
            }),
        );
    });

    printCartons$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(unassignedBoxesActions.printCartons),
                concatMap(({ codes }) => {
                    return from(codes).pipe(
                        concatMap((code) =>
                            this.httpService.printCarton(code).pipe(
                                map(() => null),
                                catchError((error: WarehouseError) => of(error.key)),
                            ),
                        ),
                        toArray(),
                        tap((keys) => {
                            const failed = keys.find((key) => key !== null);
                            if (failed) {
                                this.messageService.add(this.translateService.instant(failed), 'danger');
                            }
                        }),
                    );
                }),
            );
        },
        { dispatch: false },
    );

    assignBox$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(unassignedBoxesActions.assignBox),
            concatMap(({ cartonCode, cellCode }) => {
                return this.httpService.assignBox(cartonCode, cellCode).pipe(
                    finalizeLoader('unassigned-boxes-assign'),
                    map((payload) => unassignedBoxesActions.assignBoxSuccess({ payload })),
                    catchError((error: WarehouseError) => {
                        this.messageService.add(this.translateService.instant(error.key), 'danger');
                        return of(unassignedBoxesActions.assignBoxError({ code: error.code, key: error.key }));
                    }),
                );
            }),
        );
    });
}
